import { useMemo, useState } from "react";
import {
    Search,
    Star,
    CalendarCheck,
    Clock,
    MapPin,
    IndianRupee,
    CalendarClock,
    History,
    XCircle,
    CheckCircle2,
    RotateCcw,
    Ban,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Sample data — replace with your API / DB data. Shape stays the same.
// An "upcoming" booking lives in UPCOMING_BOOKINGS; anything finished
// (completed or cancelled) lives in ORDER_HISTORY.
// ---------------------------------------------------------------------------
const UPCOMING_BOOKINGS = [
    {
        id: "b1",
        mentorName: "Dr. Ananya Reddy",
        initials: "DA",
        course: "Additive Manufacturing",
        institution: "IIT Tirupati",
        date: "2026-07-28",
        time: "6:00 PM - 7:00 PM",
        price: 1500,
        status: "upcoming",
        location: "Online",
    },
    {
        id: "b2",
        mentorName: "Prof. Karthik Iyer",
        initials: "PK",
        course: "Robotics & Mechatronics",
        institution: "IIIT Sri City",
        date: "2026-07-30",
        time: "5:30 PM - 6:15 PM",
        price: 1800,
        status: "upcoming",
        location: "Online",
    },
    {
        id: "b3",
        mentorName: "Rahul Varma",
        initials: "RV",
        course: "EV Powertrain",
        institution: "Ashok Leyland R&D",
        date: "2026-08-02",
        time: "4:00 PM - 5:00 PM",
        price: 2200,
        status: "confirmed",
        location: "Chennai R&D Center",
    },
];

const ORDER_HISTORY = [
    {
        id: "h1",
        mentorName: "Meera Sundaram",
        initials: "MS",
        course: "PCB & Embedded Design",
        institution: "APIC Visakhapatnam",
        date: "2026-06-14",
        time: "10:00 AM - 10:45 AM",
        price: 1200,
        status: "completed",
        myRating: 5,
    },
    {
        id: "h2",
        mentorName: "Dr. Ananya Reddy",
        initials: "DA",
        course: "Additive Manufacturing",
        institution: "IIT Tirupati",
        date: "2026-05-20",
        time: "6:00 PM - 7:00 PM",
        price: 1500,
        status: "completed",
        myRating: 4,
    },
    {
        id: "h3",
        mentorName: "Prof. Karthik Iyer",
        initials: "PK",
        course: "Robotics & Mechatronics",
        institution: "IIIT Sri City",
        date: "2026-05-02",
        time: "5:30 PM - 6:15 PM",
        price: 1800,
        status: "cancelled",
        myRating: null,
    },
];

const STATUS_STYLES = {
    upcoming: "bg-blue-50 text-blue-700",
    confirmed: "bg-indigo-50 text-indigo-700",
    completed: "bg-emerald-50 text-emerald-700",
    cancelled: "bg-rose-50 text-rose-600",
};

function StatusBadge({ status }) {
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
            {status === "completed" && <CheckCircle2 className="h-3 w-3" />}
            {status === "cancelled" && <XCircle className="h-3 w-3" />}
            {(status === "upcoming" || status === "confirmed") && <Clock className="h-3 w-3" />}
            {label}
        </span>
    );
}

function formatDate(iso) {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function BookingsPage() {
    const [tab, setTab] = useState("upcoming"); // "upcoming" | "history"
    const [search, setSearch] = useState("");
    const [historyFilter, setHistoryFilter] = useState("All");

    const [upcoming, setUpcoming] = useState(UPCOMING_BOOKINGS);
    const [history, setHistory] = useState(ORDER_HISTORY);

    const filteredUpcoming = useMemo(
        () =>
            upcoming.filter(
                (b) =>
                    b.mentorName.toLowerCase().includes(search.toLowerCase()) ||
                    b.course.toLowerCase().includes(search.toLowerCase())
            ),
        [upcoming, search]
    );

    const filteredHistory = useMemo(
        () =>
            history.filter((h) => {
                const matchesSearch =
                    h.mentorName.toLowerCase().includes(search.toLowerCase()) ||
                    h.course.toLowerCase().includes(search.toLowerCase());
                const matchesStatus = historyFilter === "All" || h.status === historyFilter.toLowerCase();
                return matchesSearch && matchesStatus;
            }),
        [history, search, historyFilter]
    );

    const cancelBooking = (id) => {
        const booking = upcoming.find((b) => b.id === id);
        if (!booking) return;
        setUpcoming((prev) => prev.filter((b) => b.id !== id));
        setHistory((prev) => [{ ...booking, status: "cancelled", myRating: null }, ...prev]);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-3 sm:p-5">
            <div className="mx-auto max-w-5xl">
                {/* Tabs + search */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setTab("upcoming")}
                            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${tab === "upcoming"
                                ? "border-indigo-600 bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
                                }`}
                        >
                            <CalendarClock className="h-4 w-4" />
                            Bookings
                            <span
                                className={`rounded-full px-1.5 text-xs ${tab === "upcoming" ? "bg-white/20" : "bg-slate-100"
                                    }`}
                            >
                                {upcoming.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setTab("history")}
                            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${tab === "history"
                                ? "border-indigo-600 bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
                                }`}
                        >
                            <History className="h-4 w-4" />
                            Order History
                            <span
                                className={`rounded-full px-1.5 text-xs ${tab === "history" ? "bg-white/20" : "bg-slate-100"
                                    }`}
                            >
                                {history.length}
                            </span>
                        </button>
                    </div>

                    <div className="flex gap-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search mentor or course..."
                                className="w-56 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        {tab === "history" && (
                            <select
                                value={historyFilter}
                                onChange={(e) => setHistoryFilter(e.target.value)}
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                                <option value="All">All statuses</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        )}
                    </div>
                </div>

                {/* Upcoming bookings table */}
                {tab === "upcoming" && (
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                        <th className="px-4 py-3">Mentor / Course</th>
                                        <th className="px-4 py-3">Date & Time</th>
                                        <th className="px-4 py-3">Location</th>
                                        <th className="px-4 py-3">Price</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUpcoming.map((b) => (
                                        <tr key={b.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                                                        {b.initials}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{b.mentorName}</p>
                                                        <p className="text-xs text-slate-400">{b.course}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                <p>{formatDate(b.date)}</p>
                                                <p className="flex items-center gap-1 text-xs text-slate-400">
                                                    <Clock className="h-3 w-3" /> {b.time}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                <span className="flex items-center gap-1.5">
                                                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                                                    {b.location}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="flex items-center font-semibold text-indigo-600">
                                                    <IndianRupee className="h-3.5 w-3.5" />
                                                    {b.price.toLocaleString("en-IN")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={b.status} />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => cancelBooking(b.id)}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition-colors"
                                                >
                                                    <Ban className="h-3.5 w-3.5" />
                                                    Cancel
                                                </button>
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredUpcoming.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">
                                                No upcoming bookings match your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Order history table */}
                {tab === "history" && (
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                        <th className="px-4 py-3">Mentor / Course</th>
                                        <th className="px-4 py-3">Date & Time</th>
                                        <th className="px-4 py-3">Price</th>
                                        <th className="px-4 py-3">My Rating</th>
                                        <th className="px-4 py-3">Status</th>

                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistory.map((h) => (
                                        <tr key={h.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                                                        {h.initials}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{h.mentorName}</p>
                                                        <p className="text-xs text-slate-400">{h.course}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                <p>{formatDate(h.date)}</p>
                                                <p className="flex items-center gap-1 text-xs text-slate-400">
                                                    <Clock className="h-3 w-3" /> {h.time}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="flex items-center font-semibold text-indigo-600">
                                                    <IndianRupee className="h-3.5 w-3.5" />
                                                    {h.price.toLocaleString("en-IN")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {h.myRating ? (
                                                    <span className="flex items-center gap-1 font-medium text-slate-700">
                                                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {h.myRating}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={h.status} />
                                            </td>

                                        </tr>
                                    ))}

                                    {filteredHistory.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">
                                                No past orders match your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}